/**
 * InterbranchLinkDialog — view-only cross-branch customer link.
 *
 * Shows the customer's "Branch Credit" and the combined "Total Credit — all
 * branches" (visible to everyone). Admins/owners can search & link accounts in
 * other branches (Interbranch Link); managers + admins can Unlink a member.
 *
 * Linking NEVER moves invoices or merges balances — payments stay branch-
 * specific. Used by both /customers and /payments.
 */
import { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Link2, Link2Off, Search, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InterbranchLinkDialog({ open, onOpenChange, customer, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_super_admin;
  const isManagerOrAdmin = isAdmin || user?.role === 'manager';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [displayName, setDisplayName] = useState('');

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customer.id}/linked`);
      setData(res.data);
      setDisplayName(res.data?.display_name || customer.name || '');
    } catch {
      toast.error('Failed to load linked account');
    }
    setLoading(false);
  }, [customer]);

  useEffect(() => { if (open) { load(); setShowPicker(false); setCandidates([]); setQuery(''); } }, [open, load]);

  const runSearch = async () => {
    setSearching(true);
    try {
      const res = await api.get(`/customers/${customer.id}/link-candidates`, { params: { q: query || customer.name } });
      setCandidates(res.data?.candidates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const linkTo = async (candidateId) => {
    setBusy(true);
    try {
      const ids = [customer.id, candidateId];
      const payload = { customer_ids: ids };
      if (displayName?.trim()) payload.display_name = displayName.trim();
      await api.post('/customers/link', payload);
      toast.success('Accounts linked across branches');
      setShowPicker(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    }
    setBusy(false);
  };

  const unlink = async (memberId) => {
    setBusy(true);
    try {
      await api.post('/customers/unlink', { customer_id: memberId });
      toast.success('Account unlinked');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlink failed');
    }
    setBusy(false);
  };

  const linked = data?.linked;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="interbranch-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} className="text-teal-600" />
            Inter-branch Account · {customer?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-4">
            {/* Two totals */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-200 p-3" data-testid="branch-credit">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Branch Credit</p>
                <p className="text-lg font-bold text-slate-800">{formatPHP(data?.branch_credit || 0)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${linked ? 'border-teal-300 bg-teal-50/50' : 'border-slate-200'}`} data-testid="total-credit">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Total Credit — all branches</p>
                <p className="text-lg font-bold text-teal-700">{formatPHP(data?.total_credit || 0)}</p>
              </div>
            </div>

            {linked ? (
              <div className="space-y-2" data-testid="linked-members">
                <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                  <Link2 size={13} className="text-teal-600" /> Linked as “{data.display_name}”
                </p>
                {data.members.map(m => (
                  <div key={m.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2"
                    data-testid={`linked-member-${m.id}`}>
                    <div className="text-sm">
                      <span className="font-medium text-slate-700">{m.branch_name}</span>
                      {m.is_current && <Badge className="ml-2 bg-teal-100 text-teal-700 border-0 text-[9px]">this branch</Badge>}
                      <span className="block text-xs text-slate-400">{m.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-700">{formatPHP(m.open_balance)}</span>
                      {isManagerOrAdmin && (
                        <Button size="sm" variant="ghost" className="text-red-500 h-7"
                          disabled={busy} onClick={() => unlink(m.id)}
                          data-testid={`unlink-member-${m.id}`} title="Unlink this branch account">
                          <Link2Off size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-slate-400">
                  Balances stay branch-specific. To settle another branch's credit here, use the cross-branch payment flow.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">This customer is not linked to any other branch.</p>
            )}

            {/* Admin: Interbranch Link */}
            {isAdmin && (
              <div className="border-t border-slate-100 pt-3">
                {!showPicker ? (
                  <Button size="sm" variant="outline" onClick={() => { setShowPicker(true); runSearch(); }}
                    data-testid="open-interbranch-link">
                    <Link2 size={14} className="mr-1" /> Interbranch Link…
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={displayName} onChange={e => setDisplayName(e.target.value)}
                        placeholder="Shared display name" className="h-8 text-xs"
                        data-testid="link-display-name" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={query} onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && runSearch()}
                        placeholder="Search name in other branches" className="h-8 text-xs"
                        data-testid="link-search-input" />
                      <Button size="sm" onClick={runSearch} disabled={searching} data-testid="link-search-btn">
                        {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                      </Button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1" data-testid="link-candidates">
                      {candidates.length === 0 && !searching && (
                        <p className="text-xs text-slate-400 py-2">No matching customers in other branches.</p>
                      )}
                      {candidates.map(c => (
                        <div key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 px-2 py-1.5"
                          data-testid={`candidate-${c.id}`}>
                          <div className="text-xs">
                            <span className="font-medium text-slate-700">{c.name}</span>
                            <span className="block text-slate-400">{c.branch_name}{c.already_linked ? ' · already linked' : ''}</span>
                          </div>
                          <Button size="sm" className="h-7 bg-teal-600 hover:bg-teal-700 text-white"
                            disabled={busy} onClick={() => linkTo(c.id)} data-testid={`link-candidate-${c.id}`}>
                            <Link2 size={13} className="mr-1" /> Link
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
